import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { EnumTranslateServiceArea } from 'app/models/serviceRequest';
import {
    CreateServiceRequestType,
    ServiceRequestType,
    ServiceRequestTypePaginate,
    ServiceRequestTypePopulate,
} from 'app/models/serviceType';
import { environment } from 'environments/environment';
import { Observable } from 'rxjs';

@Injectable({
    providedIn: 'root',
})
export class ServiceTypesService {
    private baseUrl = `${environment.apiUrl}service-types`;

    constructor(private readonly _http: HttpClient) {}

    /**
     * Cria um novo tipo de solicitação de serviço.
     *
     * @param body - Dados para criar o tipo de solicitação de serviço.
     * @returns {Observable<ServiceRequestTypePopulate>} - Observable contendo o tipo de solicitação de serviço criado.
     */
    create(
        body: CreateServiceRequestType
    ): Observable<ServiceRequestTypePopulate> {
        return this._http.post<ServiceRequestTypePopulate>(
            `${this.baseUrl}`,
            body
        );
    }

    /**
     * Busca todos os tipos de solicitação de serviço.
     *
     * @returns {Observable<ServiceRequestTypePopulate[]>} - Observable contendo uma lista de tipos de solicitação de serviço.
     */
    getTypes(): Observable<ServiceRequestTypePopulate[]> {
        return this._http.get<ServiceRequestTypePopulate[]>(`${this.baseUrl}`);
    }

    /**
     * Busca os tipos de documento enumerados.
     *
     * @returns {Observable<Object>} - Observable contendo os tipos de documentos enumerados.
     */
    getEnumTypesDocument(): Observable<Object> {
        return this._http.get<Object>(
            `${environment.apiUrl}service/minimumdocuments/enum`
        );
    }

    /**
     * Busca a área de serviço.
     *
     * @returns {Observable<Object>} - Observable contendo a área de serviço.
     */
    getServiceArea(): Observable<Object> {
        return this._http.get<Object>(`${this.baseUrl}/enum`);
    }

    /**
     * Busca um tipo de solicitação de serviço pelo ID.
     *
     * @param id - ID do tipo de solicitação de serviço a ser encontrado.
     * @returns {Observable<ServiceRequestTypePopulate>} - Observable contendo o tipo de solicitação de serviço encontrado.
     */
    getById(id: String): Observable<ServiceRequestTypePopulate> {
        return this._http.get<ServiceRequestTypePopulate>(
            `${this.baseUrl}/${id}`
        );
    }

    /**
     * Busca tipos de solicitação de serviço com paginação.
     *
     * @param limit - Número de itens por página.
     * @param page - Número da página.
     * @returns {Observable<ServiceRequestTypePaginate>} - Observable contendo os tipos de solicitação de serviço paginados.
     */
    getServicesTypes(
        limit: number,
        page: number,
        name?: string,
        servicearea?: EnumTranslateServiceArea
    ): Observable<ServiceRequestTypePaginate> {
        let url = `${this.baseUrl}?limit=${limit}&skip=${page}`;

        if (name) {
            url += `&name=${name.trim()}`;
        }

        if (servicearea) {
            url += `&servicearea=${servicearea}`;
        }

        return this._http.get<ServiceRequestTypePaginate>(url);
    }

    /**
     * Atualiza um tipo de solicitação de serviço existente.
     *
     * @param id - ID do tipo de solicitação de serviço a ser atualizado.
     * @param body - Dados para atualizar o tipo de solicitação de serviço.
     * @returns {Observable<ServiceRequestType>} - Observable contendo o tipo de solicitação de serviço atualizado.
     */
    update(
        id: string,
        body: CreateServiceRequestType
    ): Observable<ServiceRequestType> {
        return this._http.patch<ServiceRequestType>(
            `${this.baseUrl}/${id}`,
            body
        );
    }

    /**
     * Deleta um tipo de solicitação de serviço pelo ID.
     *
     * @param id - ID do tipo de solicitação de serviço a ser deletado.
     * @returns {Observable<ServiceRequestType>} - Observable contendo o tipo de solicitação de serviço deletado.
     */
    delete(id: string): Observable<ServiceRequestType> {
        return this._http.delete<ServiceRequestType>(`${this.baseUrl}/${id}`);
    }
}
